import { useRef } from 'react'
import { useRouter } from 'next/router'
import { useCookies } from 'react-cookie'

import useDeckActions, { DeckAction } from 'components/deckPage/actions/menuActions'
import { useActionless, useAppSelector } from 'app-redux/hooks'
import { useCanEditDeck } from 'app-redux/deck/selectors'
import { useGetDeckHeaderArt } from 'components/deckPage/header/DeckHeader/useGetHeaderInfo'

import { TOGGLE_MODAL } from 'app-redux/active/actions/types'

import Icon from 'components/elements/Icon'

import DeckHeaderInfo from 'components/deckPage/header/DeckHeader/Info'
import OtherUsers from 'components/deckPage/header/OtherUsers'
import AvatarCircle from 'components/user/AvatarCircle'
import PhatButton from 'components/formElements/PhatButton'
import Link from 'components/elements/Link'
import CardBanner from 'components/misc/CardBanner'
import ContextMenu, { CustomMenuProps } from 'components/elements/ContextMenu'
import FloatingToolbarListOption from 'components/elements/FloatingToolbar/ListItem'
import { ExtraMenu } from 'components/elements/ArchidektDropdown/Menu'

import { encodeUsernameRoute } from 'utils/encoding'

import styles from './desktop.module.scss'

type Props = {
  className?: string
  containerClassName?: string
  hideControls?: boolean
}

const DesktopDeckHeader = ({ className = '', containerClassName = '', hideControls = false }: Props) => {
  const router = useRouter()

  const actions = useDeckActions()
  const cardBannerUrl = useGetDeckHeaderArt()
  const canEditDeck = useCanEditDeck()

  const [{ tbId: userId }] = useCookies(['tbId'])

  const deckId = useAppSelector(state => state.deck.id)
  const owner = useAppSelector(state => state.deck.owner)
  const ownerAvatar = useAppSelector(state => state.deck.ownerAvatar)

  const snapshotMeta = useAppSelector(state => state.deck.snapshotMeta)

  const [toggleModal] = useActionless(TOGGLE_MODAL)

  const playtesterButton =
    !!deckId && !snapshotMeta ? (
      <Link className={styles.playtesterLink} href={actions.playtester.to || ''}>
        <Icon name="play-circle" /> Playtester
      </Link>
    ) : (
      <PhatButton onClick={actions.playtester.onClick}>
        <Icon name="play-circle" /> Playtester
      </PhatButton>
    )

  return (
    <CardBanner src={cardBannerUrl} className={`${styles.heightenZIndex} ${className}`} allowOverflow>
      <div className={`${styles.container} ${containerClassName}`}>
        <div className={styles.content}>
          <DeckHeaderInfo />

          <div className={styles.userInfo}>
            <div className={styles.ownerInfo}>
              <AvatarCircle
                src={ownerAvatar || 'https://storage.googleapis.com/topdekt-user/avatars/default/avatar_colorless.svg'}
                username={owner}
              />
              <Link className={styles.username} href={`/u/${encodeUsernameRoute(owner)}`}>
                {owner}
              </Link>
            </div>

            <OtherUsers />
          </div>
        </div>

        {!hideControls && (
          <>
            <div className={styles.controls}>
              <div className={styles.mobilePlaytesterButton}>{playtesterButton}</div>
            </div>

            <div className={`${styles.controls} ${styles.hideOnMobile}`}>
              <div className={styles.primaryActions}>
                {!canEditDeck ? (
                  <PhatButton
                    onClick={() => {
                      if (!userId) return router.push(`/login?redirectTo=${encodeURIComponent(router.asPath)}`)

                      toggleModal({ modalName: 'cloneModal', flag: true })
                    }}>
                    <Icon name="clone" /> Clone deck
                  </PhatButton>
                ) : (
                  <PhatButton
                    onClick={() => toggleModal({ modalName: 'massEditModal', flag: true })}
                    className={styles.elevateOnEmptyDeckOverlay}>
                    <Icon name="edit outline" /> Import cards
                  </PhatButton>
                )}
                <ContextMenu
                  openOnLeftClick
                  pinToLeftFirst
                  className={styles.elevateOnEmptyDeckOverlay}
                  options={[]}
                  customMenu={<CustomMenu />}>
                  <PhatButton>
                    <Icon className={styles.extra} name="ellipsis vertical" /> More
                  </PhatButton>
                </ContextMenu>
              </div>

              <div className={styles.row}>{playtesterButton}</div>
            </div>
          </>
        )}
      </div>
    </CardBanner>
  )
}

export default DesktopDeckHeader

const CustomMenu = ({ onClose }: CustomMenuProps) => {
  const megaMenu = useRef<HTMLDivElement>(null)

  const actions = useDeckActions(onClose)

  const handleClose = () => {
    if (onClose) onClose()
  }

  return (
    <div className={styles.megaMenu} ref={megaMenu}>
      <div className={styles.subgrid}>
        <h3>Add / edit cards</h3>

        <FloatingToolbarListOption {...actions.recs} />
        <FloatingToolbarListOption {...actions.landbase} className={styles.newDeckIndicator} />
        <FloatingToolbarListOption {...actions.packages} className={styles.newDeckIndicator} />
        <FloatingToolbarListOption {...actions.precons} className={styles.newDeckIndicator} />
        <FloatingToolbarListOption {...actions.customCards} />
        <FloatingToolbarListOption {...actions.optimizePrintings} />
        <FloatingToolbarListOption {...actions.optimizeBasics} />
        <FloatingToolbarListOption {...actions.selectAll} />
      </div>

      <div>
        <div className={styles.subgrid}>
          <h3>Deck info</h3>

          {!actions.settings.hidden && (
            <ExtraMenu
              parentRef={megaMenu.current}
              option={{
                icon: actions.settings.icon,
                label: actions.settings.label,
                id: 'settings-more',
                type: 'extras',
                extraTriggerClassName: styles.extraMenu,
                options: [
                  { ...actions.mainSettings },
                  { ...actions.description, icon: undefined },
                  { ...actions.deckTags },
                  { ...actions.collaborators },
                  { ...actions.categories },
                ],
              }}
              unFocusClick={(_, onClick) => {
                if (onClick) onClick()

                handleClose()
              }}
            />
          )}

          {!actions.history.hidden && (
            <ExtraMenu
              parentRef={megaMenu.current}
              option={{
                icon: actions.history.icon,
                label: actions.history.label,
                id: 'history-more',
                type: 'extras',
                extraTriggerClassName: styles.extraMenu,
                options: [
                  { ...actions.recentChanges },
                  { ...actions.addRemoveCards },
                  { ...actions.snapshots },
                  { type: 'spacer', hidden: actions.quickSnapshot.hidden },
                  { ...actions.quickSnapshot },
                ],
              }}
              unFocusClick={(_, onClick) => {
                if (onClick) onClick()

                handleClose()
              }}
            />
          )}
          <FloatingToolbarListOption {...actions.deckHelp} />
          <FloatingToolbarListOption {...actions.stats} />
          <FloatingToolbarListOption {...actions.compare} />
          <FloatingToolbarListOption {...actions.combos} />
        </div>

        <div className={styles.subgrid}>
          <h3>Misc</h3>

          <FloatingToolbarListOption {...actions.export} />
          <FloatingToolbarListOption {...actions.buySell} />
          <FloatingToolbarListOption {...actions.bookmark} />
          <FloatingToolbarListOption {...actions.clone} />
          <FloatingToolbarListOption {...actions.share} />
          <FloatingToolbarListOption {...actions.addToCollection} />
          <FloatingToolbarListOption {...actions.keybinds} />

          <ExtraMenu
            parentRef={megaMenu.current}
            option={{
              icon: 'arrow alternate circle right outline',
              label: 'Open deck in',
              id: 'deck-links-more',
              type: 'extras',
              extraTriggerClassName: styles.extraMenu,
              options: [{ ...actions.containingFolder }, { ...actions.sandbox }],
            }}
            unFocusClick={(_, onClick) => {
              if (onClick) onClick()

              handleClose()
            }}
          />
        </div>
      </div>
    </div>
  )
}
